import {
  CombinedStepForTree,
  LinkedWorkflowStep,
  TemplateStepPreview,
  TreeNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  ExpirationIntervals,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  CountryCodeLower,
  GovernmentIdEnabledIdClassByCountryEntry,
  SelfieMatch,
} from 'dashboard/models/verification-template-version';
import {
  WorkflowConditional,
  WorkflowStep,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { CountryCode, InquiryLanguage } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiComponentConfig } from 'lib/interfaces/ui-components';
import { RedirectQueryStringConfig } from 'verify/models/inquiry';

export type SetDiff<T> = {
  added: T[] | null;
  removed: T[] | null;
};

export type ValueDiff<T> = {
  added: T | null;
  removed: T | null;
};

export enum VersionDiffChangeType {
  ADDED = 'added',
  REMOVED = 'removed',
  MODIFIED = 'modified',
}

export enum Column {
  BASE = 'base',
  TARGET = 'target',
}

export type HashDiff<T extends string | number | symbol> = {
  [key in T]?: ChangeValue;
};

// TODO(Ernest): further restrict `value` type once we have a better idea of what it can be
export type ChangeValue = {
  'change-type': VersionDiffChangeType;
  value: any;
  'prev-value': any;
};

export type ObjectDiff<M, C, T = VersionDiffChangeType> = {
  'change-type': T;
  meta: M;
  changes: C;
};

type AddedOrRemovedObjectDiff<M, C = HashDiff<string>> = ObjectDiff<
  { value: M },
  C,
  VersionDiffChangeType.ADDED | VersionDiffChangeType.REMOVED
>;

export type ModifiedObjectDiff<M, C = HashDiff<string>> = ObjectDiff<
  { 'target-value': M; 'base-value': M },
  C,
  VersionDiffChangeType.MODIFIED
>;

export type ObjectArrayDiff<M, C = HashDiff<string>> = Record<
  string,
  AddedOrRemovedObjectDiff<M, C> | ModifiedObjectDiff<M, C>
>;
export type CustomObjectArrayDiff<D> = Record<string, D>;
export type PersonaObjectArrayDiff =
  | WorkflowVersionArrayDiff
  | WorkflowVersionStepArrayDiff
  | StepsArrayDiff
  | VerificationTemplateVersionArrayDiff
  | IdClassesByCountryDiff;

export type WorkflowStepWithPath = WorkflowStep & {
  // json_path of workflow_step in reference to steps_config
  // eg. path: '0.branches.2.0'
  path: string;
};

export type WorkflowVersionStepArrayDiff = CustomObjectArrayDiff<WorkflowVersionStepDiff>;
type WorkflowVersionStepDiffChanges = {
  // Only applies to conditional steps
  conditionals: ObjectArrayDiff<WorkflowConditional> | undefined;
  branches: ValueDiff<number> | undefined;
  step: HashDiff<string>;
};
export type WorkflowVersionStepDiff =
  | AddedOrRemovedObjectDiff<WorkflowStepWithPath, WorkflowVersionStepDiffChanges>
  | ModifiedObjectDiff<WorkflowStepWithPath, WorkflowVersionStepDiffChanges>;

export type WorkflowVersionArrayDiff = CustomObjectArrayDiff<WorkflowVersionDiff>;
export type WorkflowVersionDiff = ObjectDiff<
  {
    'step-names': string[];
  },
  {
    'steps-config': WorkflowVersionStepArrayDiff;
  }
>;

export type StepsArrayDiff = CustomObjectArrayDiff<StepDiff>;
export type StepDiff = ObjectDiff<
  {
    'name-display': string;
  },
  {
    'hide-cancel-button': ValueDiff<boolean>;
    'back-step': ValueDiff<string>;
    components: ObjectArrayDiff<UiComponentConfig>;
    'capture-methods': SetDiff<string>;
    'device-handoff-options': SetDiff<string>;
  }
>;

export type EnabledIdClassesByCountry = {
  country: CountryCodeLower;
  'id-classes': Array<GovernmentIdEnabledIdClassByCountryEntry>;
};

export type IdClassesByCountryDiff = ObjectArrayDiff<
  EnabledIdClassesByCountry,
  { 'id-classes': IdClassDiffArrayDiff }
>;

export type IdClassDiffArrayDiff = CustomObjectArrayDiff<IdClassDiff>;
export type IdClassDiff = ObjectDiff<
  Record<string, never>,
  {
    'requires-sides': SetDiff<string>;
    'requires-device': SetDiff<string>;
    'accepted-classes': SetDiff<string>;
  }
>;

export type VerificationTemplateVersionArrayDiff =
  CustomObjectArrayDiff<VerificationTemplateVersionDiff>;
export type VerificationTemplateVersionDiff = ObjectDiff<
  {
    name: string;
  },
  {
    'verification-checks': ObjectArrayDiff<VerificationCheckConfig>;
    'enabled-id-classes-by-country': IdClassesByCountryDiff | null;
    'selfie-match': ValueDiff<SelfieMatch> | null;
    'num-attempts': ValueDiff<number> | null;
    name: ValueDiff<string> | null;
  }
>;

export type CountryCodeMap = Record<string, SetDiff<CountryCode>>;
export type Connector = {
  'from-step': string;
  'to-step': string;
  'workflow-or-component-token': string;
};

export type ConnectorsDiff = {
  target: {
    added: Connector[];
    modified: Connector[];
  };
  base: {
    removed: Connector[];
    modified: Connector[];
  };
};

export type InquiryTemplateVersionDiff = {
  'allowed-hosts': SetDiff<string>;
  'country-codes': CountryCodeMap;
  'disable-client-create': ValueDiff<boolean>;
  'enabled-locales': SetDiff<InquiryLanguage>;
  'expiration-intervals': HashDiff<keyof ExpirationIntervals>;
  'field-schema': ObjectArrayDiff<FieldSchema>;
  'redirect-query-string-config': HashDiff<keyof RedirectQueryStringConfig>;
  'redirect-uri': ValueDiff<string>;
  'verification-template-versions': VerificationTemplateVersionArrayDiff;
  'workflow-versions': WorkflowVersionArrayDiff;
  steps: StepsArrayDiff;
  theme: HashDiff<string>;
  connectors: ConnectorsDiff;
};

export type BaseInquiryTemplateVersionData = {
  fieldSchema: FieldSchema[];
  workflowVersions: Record<string, WorkflowVersion>;
  steps: InquiryTemplateVersionStep[];
  stepsPreview: StepPreview[];
};

export type VersionDiff = {
  inquiryTemplateVersionDiff: InquiryTemplateVersionDiff;
  baseInquiryTemplateVersionData: BaseInquiryTemplateVersionData;
};

export type CombinedVersionDiffStepForTree = CombinedStepForTree & {
  parentStepName?: string;
  workflowOrComponentToken?: string;
};

export interface VersionDiffTreeNode extends Omit<TreeNode, 'children'> {
  children: {
    target: string;
    workflowOrComponentToken: string | undefined;
  }[];
}

export interface VersionDiffLinkedWorkflowStep extends LinkedWorkflowStep {
  // if the workflow version contains an update next step action,
  // this will be the token of that step
  updateInquiryNextStepActionStepToken?: string;
}

export type VersionDiffCombinedStep = TemplateStepPreview | VersionDiffLinkedWorkflowStep;
